'use client';

import type { Location } from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  CardContent,
  cn,
  ghostAccentIconTileClass,
  healthCriticalIconTileClass,
  healthCriticalSurfaceClass,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { MapPin, Plus } from 'lucide-react';
import type { KeyboardEvent, ReactNode } from 'react';

import {
  ListEmptyState,
  ResourceCardGrid,
  ResourceCardGridItem,
  resourceInteractiveCardClass,
} from '@/core/components/layout';
import { GridAddCard } from '@/core/components/layout/grid-add-card';

import {
  formatLocationReviewSummary,
  locationNeedsAction,
  type LocationHealthSummary,
  type LocationReviewStats,
} from './location-health';
import {
  LocationHealthDots,
  type LocationHealthLabels,
} from './location-health-indicators';

export type LocationRow = Pick<
  Location,
  | 'id'
  | 'name'
  | 'address'
  | 'externalLinks'
  | 'reviewExternalIds'
  | 'reviewChannelOptOut'
>;

export type LocationReviewLabels = {
  summary: string;
  summaryAria: string;
};

interface LocationsCardGridProps {
  locations: LocationRow[];
  healthById: Record<string, LocationHealthSummary>;
  healthLabels: LocationHealthLabels;
  /** Aggregated review stats by location id (all synced providers). */
  reviewStatsById?: Record<string, LocationReviewStats>;
  reviewLabels?: LocationReviewLabels;
  locale?: string;
  /** Reserve equal-height review meta slot when reputation is available. */
  showReviewSlot?: boolean;
  /** Hide Google/sync chips and red interrupt when reputation is locked. */
  includeReputation?: boolean;
  isLoading?: boolean;
  searchQuery?: string;
  emptyTitle: string;
  emptyDescription?: string;
  noResultsTitle: string;
  noResultsDescription?: string;
  /** Optional empty-state action (overrides default create button). */
  emptyAction?: ReactNode;
  createLabel?: string;
  /** Dashed grid slot label (e.g. “Add location”). Falls back to createLabel. */
  addCardLabel?: string;
  canCreate?: boolean;
  onCreate?: () => void;
  onLocationClick: (location: LocationRow) => void;
}

/** Two lines of `--t-body` so short addresses don't shorten the card. */
const ADDRESS_CLAMP_CLASS =
  'line-clamp-2 min-h-10 text-[13.5px] leading-relaxed text-muted-foreground';

/** Reserved footer for labeled health badges — keeps equal card heights. */
const HEALTH_FOOTER_CLASS = 'mt-auto flex min-h-6 shrink-0 items-center';

/** One compact review line (Status Board signal, not a metric wall). */
const REVIEW_META_CLASS =
  'min-h-5 shrink-0 text-[11px] font-semibold uppercase tracking-[0.6px] tabular-nums text-muted-foreground';

function LocationCard({
  location,
  health,
  healthLabels,
  reviewStats,
  reviewLabels,
  locale,
  showReviewSlot,
  includeReputation = true,
  onLocationClick,
}: {
  location: LocationRow;
  health: LocationHealthSummary | undefined;
  healthLabels: LocationHealthLabels;
  reviewStats: LocationReviewStats | undefined;
  reviewLabels: LocationReviewLabels | undefined;
  locale: string;
  showReviewSlot: boolean;
  includeReputation: boolean;
  onLocationClick: (location: LocationRow) => void;
}) {
  const needsAction = health
    ? locationNeedsAction(health, { includeReputation })
    : false;
  const hasReviewStats =
    !!reviewStats && reviewStats.totalReviews > 0 && !!reviewLabels;
  const reviewSummary = hasReviewStats
    ? formatLocationReviewSummary(reviewStats, reviewLabels.summary, locale)
    : null;
  const reviewAria = hasReviewStats
    ? formatLocationReviewSummary(reviewStats, reviewLabels.summaryAria, locale)
    : undefined;

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onLocationClick(location);
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={location.name}
      className={cn(
        'flex h-full cursor-pointer flex-col',
        resourceInteractiveCardClass,
        needsAction && healthCriticalSurfaceClass,
      )}
      onClick={() => onLocationClick(location)}
      onKeyDown={handleKeyDown}
    >
      <CardContent className="flex h-full flex-1 flex-col gap-3 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-[var(--r-control)]',
              needsAction
                ? healthCriticalIconTileClass
                : ghostAccentIconTileClass,
            )}
            aria-hidden
          >
            <MapPin className="size-4" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h3 className="truncate text-[15.5px] font-bold leading-tight text-foreground text-balance">
              {location.name}
            </h3>
            <p className={ADDRESS_CLAMP_CLASS}>{location.address || '—'}</p>
            {showReviewSlot ? (
              reviewSummary ? (
                <p className={REVIEW_META_CLASS} aria-label={reviewAria}>
                  {reviewSummary}
                </p>
              ) : (
                <div className={REVIEW_META_CLASS} aria-hidden />
              )
            ) : null}
          </div>
        </div>

        <div className={HEALTH_FOOTER_CLASS}>
          {health ? (
            <LocationHealthDots
              health={health}
              labels={healthLabels}
              includeReputation={includeReputation}
            />
          ) : (
            <span className="invisible h-6 w-full" aria-hidden />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function LocationsCardGrid({
  locations,
  healthById,
  healthLabels,
  reviewStatsById,
  reviewLabels,
  locale = 'en',
  showReviewSlot = false,
  includeReputation = true,
  isLoading,
  searchQuery,
  emptyTitle,
  emptyDescription,
  noResultsTitle,
  noResultsDescription,
  emptyAction,
  createLabel,
  addCardLabel,
  canCreate,
  onCreate,
  onLocationClick,
}: LocationsCardGridProps) {
  const isSearch = !!searchQuery?.trim();
  const showAddCard =
    !!canCreate && !!onCreate && !!(addCardLabel || createLabel) && !isSearch;

  if (isLoading) {
    return (
      <ResourceCardGrid slotCount={3} asList={false} aria-busy>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="h-full min-h-0">
            <Card className="flex h-full flex-col">
              <CardContent className="flex h-full flex-1 flex-col gap-3 p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <Skeleton className="size-10 shrink-0 rounded-[var(--r-control)]" />
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-10 w-full max-w-[12rem]" />
                    {showReviewSlot ? <Skeleton className="h-4 w-28" /> : null}
                  </div>
                </div>
                <div className={HEALTH_FOOTER_CLASS}>
                  <Skeleton className="h-6 w-40" />
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </ResourceCardGrid>
    );
  }

  if (locations.length === 0) {
    const defaultCreateAction =
      !isSearch && canCreate && onCreate && createLabel ? (
        <Button onClick={onCreate} data-guidance-target="locations-empty">
          <Plus className="mr-2 h-4 w-4" />
          {createLabel}
        </Button>
      ) : undefined;

    return (
      <ListEmptyState
        icon={<MapPin className="h-6 w-6" />}
        title={isSearch ? noResultsTitle : emptyTitle}
        description={isSearch ? noResultsDescription : emptyDescription}
        action={emptyAction ?? defaultCreateAction}
      />
    );
  }

  const slotCount = locations.length + (showAddCard ? 1 : 0);

  return (
    <ResourceCardGrid slotCount={slotCount}>
      {locations.map((location) => (
        <ResourceCardGridItem key={location.id}>
          <LocationCard
            location={location}
            health={healthById[location.id]}
            healthLabels={healthLabels}
            reviewStats={reviewStatsById?.[location.id]}
            reviewLabels={reviewLabels}
            locale={locale}
            showReviewSlot={showReviewSlot}
            includeReputation={includeReputation}
            onLocationClick={onLocationClick}
          />
        </ResourceCardGridItem>
      ))}
      {showAddCard && onCreate ? (
        <ResourceCardGridItem>
          <GridAddCard
            label={addCardLabel || createLabel || ''}
            onClick={onCreate}
            contentClassName="min-h-0 p-5 sm:p-6"
            data-guidance-target="locations-add-card"
          />
        </ResourceCardGridItem>
      ) : null}
    </ResourceCardGrid>
  );
}
